import React from "react";
import { motion } from "framer-motion";
import {
  FaLaptopCode,
  FaMobileAlt,
  FaShoppingCart,
  FaGlobe,
  FaSearch,
  FaCogs,
  FaPencilRuler,
  FaCode,
  FaBug,
  FaRocket,
} from "react-icons/fa";
import "./WebDevelopment.scss"; 
import ServicesCommon from "../ServicesCommon/ServicesCommon";
import { useNavigate } from "react-router-dom";
// Note: We'll use a placeholder for the actual image path
import WebDevImg  from "../../../assets/images/webdevelopment.png";

// --- Framer Motion Variants ---

const sectionFade = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.17, 0.55, 0.55, 1] },
  },
};

const listContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const WebDevelopment = () => {
  const navigate = useNavigate();

  // Data from user's provided images/context
  const coreServices = [
    {
      icon: FaMobileAlt,
      title: "Responsive Pixel Perfect Design",
      detail:
        "Econsign soft craft splendid and deliver experiences that shines. We help startups & businesses with creative, intuitive, immersive UI/UX designs that seamlessly guide users in performing the desired actions flawlessly. We help brands create brilliant designs and superior user experiences through UX research, behavioral analytics and market insights.",
    },
    {
      icon: FaCogs,
      title: "24/7 Smart Support",
      detail: "We at Econsign Soft are always available for quick smart support 24/7.",
    },
    {
      icon: FaSearch,
      title: "SEO-Optimized Codebase",
      detail: "Building sites with clean, semantic HTML and efficient performance metrics for top search engine ranking.",
    },
  ];

  // Data for Web Development Process
  const processSteps = [
    { icon: FaPencilRuler, title: "Planning & Strategy" },
    { icon: FaLaptopCode, title: "Design & Prototyping" },
    { icon: FaCode, title: "Development & Coding" },
    { icon: FaBug, title: "Testing & Quality Check" },
    { icon: FaRocket, title: "Deployment & Launch" },
  ];

  return (
    <div className="web-dev-page">
      {/* ------------------ 1. HERO SECTION ------------------ */}
      <section className="hero-section-web-dev">
        <div className="hero-content-left">
          <motion.p
            className="hero-tag-web-dev"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            WEB DEVELOPMENT
          </motion.p>

          <motion.h1
            className="hero-title-web-dev"
            variants={sectionFade}
            initial="hidden"
            animate="visible"
          >
            Building the Digital Future of Your{" "}
            <motion.span
              initial={{ color: "#333" }}
              animate={{ color: "#895DF0" }} // Purple accent
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: "mirror",
              }}
            >
              Business
            </motion.span>
          </motion.h1>

          <motion.p
            className="hero-subtext-web-dev"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            Econsign Soft is the leading full-stack web development company which can provide many advantages for any company.
          </motion.p>

          <motion.p
            className="hero-desc-web-dev"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            Full stack development refers to the end-to-end application software development, including the front end and back end. The front end consists of the user interface, and the back end takes care of the business logic and application workflows. We help in choosing the right technologies for the project development and testing both on the front end and the back end. We write clean code across the stack by following the best practices of the tools used.
          </motion.p>

          <motion.button
            className="primary-btn-web-dev"
            variants={listItem}
            initial="hidden"
            animate="visible"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 30px rgba(137, 93, 240, 0.4)",
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/contact-us")}
          >
           Contact Us
          </motion.button>
        </div>

        {/* Image Placeholder on Right */}
        <motion.div
          className="web-dev-illustration"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 0.5 }}
        >
          <img
            src={WebDevImg}
            alt="Web Development Illustration"
            className="responsive-web-img"
          />
     
        </motion.div>
      </section>
      
      {/* ------------------ 2. CORE WEB SERVICES SECTION ------------------ */}
      <section className="core-services-section">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Our <span className="highlight-services">Core</span> Web Development Services
        </motion.h2>

        <motion.div
          className="services-grid"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {coreServices.map((service, i) => (
            <motion.div className="service-card" key={i} variants={listItem}>
              <service.icon className="service-icon" />
              <h3>{service.title}</h3>
              <p>{service.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ------------------ 3. WEB DEVELOPMENT PROCESS ------------------ */}
      <section className="web-process-section">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          How We Build Your <span className="highlight-process">Perfect</span> Website
        </motion.h2>

        <motion.div
          className="process-timeline"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {processSteps.map((step, i) => (
            <motion.div className="process-step" key={i} variants={listItem}>
              <div className="step-content-wrapper">
                <span className="step-number">{i + 1}</span>
                <step.icon className="step-icon" />
                <h4>{step.title}</h4>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

    </div>
  );
};

export default WebDevelopment;